// Capa HTTP y lógica pura de la fase 2 de la generación del informe: la
// valorización (precio por línea, castigos de liquidación, tipo de cambio).
//
// NO HAY ARITMÉTICA DE VALORACIÓN ACÁ, y es a propósito: los totales, las
// conversiones entre monedas y la liquidación los calcula el SERVIDOR con
// `brick/math` y viajan ya formateados como los imprime el informe. Esta capa
// los pinta; calcularlos de nuevo en coma flotante sería un segundo método de
// tasación que tarde o temprano discrepa del que se guarda.

import { apiFetch } from "./api";
import type { Hecho } from "./generacion";
import type { Moneda } from "./informes";
import { numeroEscrito } from "./numeroEscrito";

/* ---------------------------------------------------------------------------
   Contrato del API (4b, valorización). Los nombres son los del servidor.
--------------------------------------------------------------------------- */

export type RubroDeValorizacion =
  | "suelos"
  | "aguas"
  | "plantaciones"
  | "construcciones"
  | "instalaciones"
  | "maquinaria";

export type UnidadDeLinea = "ha" | "l/s" | "m2" | "un";

export const UNIDADES_DE_LINEA: UnidadDeLinea[] = ["ha", "l/s", "m2", "un"];

/** Un monto en las tres monedas; `null` donde no se pudo calcular. */
export interface MontoEnTresMonedas<T> {
  uf: T | null;
  clp: T | null;
  usd: T | null;
}

export type EstadoCalculado = "cuenta" | "incluida" | "sin_precio" | "sin_cantidad" | "huerfana";

export interface LineaValorizada {
  id: number;
  descripcion: string;
  manual: boolean;
  origen: { tipo: string; id: number } | null;
  fuente_eliminada: boolean;
  cantidad: number | null;
  unidad: string | null;
  cantidad_cambio: { anterior: number | null; en: string } | null;
  precio: {
    valor: number;
    moneda: Moneda | null;
    motivo: string | null;
    fijado_por: { id: number; nombre: string } | null;
    fijado_en: string | null;
  } | null;
  propuesta: {
    valor: number;
    moneda: Moneda | null;
    origen: string | null;
    desviacion_pct: number | null;
  } | null;
  incluido_en: string | null;
  estado: EstadoCalculado;
  total: MontoEnTresMonedas<number>;
  total_formateado: MontoEnTresMonedas<string>;
}

export interface RubroValorizado {
  rubro: RubroDeValorizacion;
  etiqueta: string;
  nota: string | null;
  lineas: LineaValorizada[];
  valor: MontoEnTresMonedas<number>;
  valor_formateado: MontoEnTresMonedas<string>;
  lineas_valorizadas: number;
  lineas_incluidas: number;
  lineas_sin_precio: number;
  lineas_sin_cantidad: number;
  castigo: { pct: number | null; origen: "rubro" | "global" | null; propio: number | null };
  liquidacion: MontoEnTresMonedas<number>;
  liquidacion_formateada: MontoEnTresMonedas<string>;
}

export interface PendienteDeValorizacion {
  codigo: string;
  rubro?: RubroDeValorizacion;
  cantidad?: number;
  detalle: string;
}

export interface Valorizacion {
  tipo_cambio: { uf: number | null; usd: number | null; fecha_valorizacion: string | null };
  superficie_ha: number | null;
  rubros: RubroValorizado[];
  total: MontoEnTresMonedas<number>;
  total_formateado: MontoEnTresMonedas<string>;
  valor_por_ha_uf: number | null;
  valor_por_ha_formateado: string | null;
  liquidacion: {
    castigo_global_pct: number | null;
    total: MontoEnTresMonedas<number>;
    total_formateado: MontoEnTresMonedas<string>;
  };
  avaluo_fiscal: {
    total_clp: number | null;
    relacion_valor_comercial: number | null;
    total_formateado: string | null;
  };
  pendientes: PendienteDeValorizacion[];
  sincronizacion: { creadas: number; actualizadas: number; retiradas: number; huerfanas: number } | null;
}

/** Cada escritura devuelve la valorización entera (lista para pintar) y los hechos que movió. */
export interface RespuestaDeEscritura {
  data: Valorizacion;
  cambios?: unknown;
}

export interface CuerpoDelPrecio {
  precio_unitario?: number | null;
  moneda?: Moneda;
  motivo?: string;
  incluido_en?: string | null;
}

export interface CuerpoDeLineaManual {
  rubro: RubroDeValorizacion;
  descripcion: string;
  cantidad: number;
  unidad: UnidadDeLinea;
  precio_unitario?: number;
  moneda?: Moneda;
}

export interface ResultadoDelTipoDeCambio {
  data: Hecho[];
  /** Los indicadores que no se pudieron resolver: `uf`, `usd`. */
  pendientes: ("uf" | "usd")[];
  errores: Record<string, Record<string, string>>;
}

/* ---------------------------------------------------------------------------
   HTTP
--------------------------------------------------------------------------- */

const base = (informeId: number) => `/api/informes/${informeId}/valorizacion`;

export async function fetchValorizacion(informeId: number): Promise<Valorizacion> {
  const r = await apiFetch<{ data: Valorizacion }>(base(informeId));
  return r.data;
}

export async function fijarPrecioRequest(
  informeId: number,
  lineaId: number,
  cuerpo: CuerpoDelPrecio,
): Promise<Valorizacion> {
  const r = await apiFetch<RespuestaDeEscritura>(`${base(informeId)}/lineas/${lineaId}`, {
    method: "PUT",
    body: JSON.stringify(cuerpo),
  });
  return r.data;
}

export async function aceptarPropuestaRequest(informeId: number, lineaId: number): Promise<Valorizacion> {
  const r = await apiFetch<RespuestaDeEscritura>(`${base(informeId)}/lineas/${lineaId}/aceptar-propuesta`, {
    method: "POST",
  });
  return r.data;
}

export async function fijarCastigosRequest(
  informeId: number,
  cuerpo: { global?: number | null; por_rubro?: Partial<Record<RubroDeValorizacion, number | null>> },
): Promise<Valorizacion> {
  const r = await apiFetch<RespuestaDeEscritura>(`${base(informeId)}/castigos`, {
    method: "PUT",
    body: JSON.stringify(cuerpo),
  });
  return r.data;
}

export async function crearLineaManualRequest(informeId: number, cuerpo: CuerpoDeLineaManual): Promise<Valorizacion> {
  const r = await apiFetch<RespuestaDeEscritura>(`${base(informeId)}/lineas`, {
    method: "POST",
    body: JSON.stringify(cuerpo),
  });
  return r.data;
}

export async function eliminarLineaRequest(informeId: number, lineaId: number): Promise<Valorizacion> {
  const r = await apiFetch<RespuestaDeEscritura>(`${base(informeId)}/lineas/${lineaId}`, { method: "DELETE" });
  return r.data;
}

export async function fijarTipoDeCambioRequest(informeId: number, fecha: string): Promise<ResultadoDelTipoDeCambio> {
  return apiFetch<ResultadoDelTipoDeCambio>(`/api/informes/${informeId}/tipo-de-cambio`, {
    method: "POST",
    body: JSON.stringify({ fecha }),
  });
}

/* ---------------------------------------------------------------------------
   Formato
--------------------------------------------------------------------------- */

const SIMBOLO: Record<Moneda, string> = { UF: "UF", CLP: "$", USD: "US$" };

/** Un precio unitario admite hasta seis decimales (la columna los guarda). */
// Con "de-DE" y no "es-CL": es-CL no agrupa los números de cuatro cifras
// ("1234"), y un millar sin punto se lee distinto de los que sí lo llevan. Los
// separadores son los mismos (punto de miles, coma decimal).
const FORMATO_DE_PRECIO = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 6,
});

/**
 * Un precio con su moneda adelante, o "—". El símbolo acompaña a la cifra
 * siempre: "$ 1.639" sobre un monto en UF ya mordió una vez en este sistema.
 */
export function formatearPrecio(valor: number | null, moneda: Moneda): string {
  if (valor === null) return "—";

  return `${SIMBOLO[moneda]} ${FORMATO_DE_PRECIO.format(valor)}`;
}

/**
 * Una cantidad con su unidad ("20,1 ha"), o `null` si no hay cantidad: la
 * cantidad que falta se dice como tal y nunca se imprime como un cero.
 */
export function formatearCantidad(cantidad: number | null, unidad: string | null): string | null {
  if (cantidad === null) return null;

  const cifra = FORMATO_DE_PRECIO.format(cantidad);

  return unidad ? `${cifra} ${unidad}` : cifra;
}

/** La unidad con que se mide cada rubro: la que se ofrece al crear una línea a mano. */
export function unidadPorDefecto(rubro: RubroDeValorizacion): UnidadDeLinea {
  switch (rubro) {
    case "suelos":
    case "plantaciones":
      return "ha";
    case "aguas":
      return "l/s";
    case "construcciones":
      return "m2";
    default:
      return "un";
  }
}

/**
 * Un número tal como se vuelve a teclear: coma decimal y SIN separador de
 * miles. Con punto de miles, reabrir un precio guardado reintroduciría la
 * ambigüedad que `numeroEscrito` tiene que resolver con reglas.
 */
export function textoDeNumero(valor: number): string {
  return String(valor).replace(".", ",");
}

/** "—" para lo que el servidor no pudo calcular; nunca un 0 inventado. */
export function textoOGuion(valor: string | null | undefined): string {
  return valor ?? "—";
}

/* ---------------------------------------------------------------------------
   Lo que se escribe: precio y castigo
--------------------------------------------------------------------------- */

const PRECIO_MAXIMO = 99_999_999_999_999;

export type LecturaDelPrecio =
  | { estado: "vacio" }
  | { estado: "invalido"; mensaje: string }
  | { estado: "ok"; valor: number; lectura: string };

/**
 * Lo escrito en el campo de precio, y cómo se entendió.
 *
 * Un punto leído como decimal convierte 24.000.000 en 24, y la línea queda
 * perfectamente formada: por eso se MUESTRA la lectura ("Se guardará
 * $ 24.000.000") y la persona resuelve a la vista la única ambigüedad que queda.
 */
export function interpretarPrecio(texto: string, moneda: Moneda): LecturaDelPrecio {
  if (texto.trim() === "") return { estado: "vacio" };

  const valor = numeroEscrito(texto);

  if (valor === null) return { estado: "invalido", mensaje: "Escribe un número, por ejemplo 24.000.000 o 1.234,5." };
  if (valor < 0) return { estado: "invalido", mensaje: "El precio no puede ser negativo." };
  if (valor > PRECIO_MAXIMO) return { estado: "invalido", mensaje: "El precio es demasiado grande." };
  if (Math.round(valor * 1e6) / 1e6 !== valor) {
    return { estado: "invalido", mensaje: "El precio admite hasta seis decimales." };
  }

  return { estado: "ok", valor, lectura: `Se guardará ${formatearPrecio(valor, moneda)}` };
}

export type LecturaDelCastigo =
  | { estado: "vacio" }
  | { estado: "invalido"; mensaje: string }
  | { estado: "ok"; valor: number };

/** Un castigo de 0 a 100 con hasta dos decimales. Vacío = sin castigo propio. */
export function interpretarCastigo(texto: string): LecturaDelCastigo {
  if (texto.trim() === "") return { estado: "vacio" };

  const valor = numeroEscrito(texto);

  if (valor === null) return { estado: "invalido", mensaje: "El castigo debe ser un número." };
  if (valor < 0 || valor > 100) return { estado: "invalido", mensaje: "El castigo debe estar entre 0 y 100." };
  if (Math.round(valor * 100) / 100 !== valor) {
    return { estado: "invalido", mensaje: "El castigo admite hasta dos decimales." };
  }

  return { estado: "ok", valor };
}

/* ---------------------------------------------------------------------------
   Estado de una línea
--------------------------------------------------------------------------- */

export type EstadoDeLaLinea = "propuesta" | "aceptado" | "corregido" | "ingresado" | "falta_valor";

export const ETIQUETA_ESTADO_DE_LINEA: Record<EstadoDeLaLinea, string> = {
  propuesta: "Propuesta",
  aceptado: "Aceptado",
  corregido: "Corregido",
  ingresado: "Ingresado",
  falta_valor: "Falta valor",
};

/**
 * Qué decir de una línea. La moneda cuenta: el mismo número en otra moneda no
 * es la propuesta aceptada, y el servidor también lo trata como una corrección.
 */
export function estadoDeLaLinea(linea: LineaValorizada): EstadoDeLaLinea {
  const { precio, propuesta } = linea;

  if (precio === null) return propuesta === null ? "falta_valor" : "propuesta";
  if (propuesta === null) return "ingresado";

  return precio.valor === propuesta.valor && precio.moneda === propuesta.moneda ? "aceptado" : "corregido";
}

/** El color de cada estado en el diseño: Propuesta ámbar, Aceptado verde, Corregido e Ingresado azules. */
export const TONO_DE_ESTADO_DE_LINEA: Record<EstadoDeLaLinea, "warning" | "success" | "info" | "danger"> = {
  propuesta: "warning",
  aceptado: "success",
  corregido: "info",
  ingresado: "info",
  falta_valor: "danger",
};

/** Lo que más falta manda: el estado de un rubro es el de su línea más atrasada. */
const PRIORIDAD_DEL_ESTADO: EstadoDeLaLinea[] = ["falta_valor", "propuesta", "corregido", "ingresado", "aceptado"];

export function estadoDelRubro(rubro: Pick<RubroValorizado, "lineas">): EstadoDeLaLinea {
  const estados = rubro.lineas.map(estadoDeLaLinea);

  return PRIORIDAD_DEL_ESTADO.find((e) => estados.includes(e)) ?? "falta_valor";
}

/**
 * La propuesta del RUBRO, solo cuando TODAS sus líneas tienen una, en UF y con
 * cantidad: sumar una propuesta que cubre media tabla afirmaría un valor que el
 * sistema no calculó, y mezclar monedas exigiría un tipo de cambio que acá no se
 * aplica. El origen es el de las líneas si coinciden.
 */
export function propuestaDelRubro(rubro: Pick<RubroValorizado, "lineas">): { uf: number; origen: string } | null {
  if (rubro.lineas.length === 0) return null;

  let uf = 0;
  const origenes = new Set<string>();

  for (const linea of rubro.lineas) {
    const p = linea.propuesta;
    if (p === null || p.moneda !== "UF" || linea.cantidad === null) return null;

    uf += p.valor * linea.cantidad;
    origenes.add(p.origen ?? "");
  }

  const unico = origenes.size === 1 ? [...origenes][0] : "";

  return {
    uf,
    origen: unico !== "" ? unico : `${rubro.lineas.length} líneas con orígenes distintos`,
  };
}

/** ¿Este precio se aparta de la propuesta de la línea? Entonces el motivo es obligatorio. */
export function exigeMotivo(linea: LineaValorizada, valor: number, moneda: Moneda): boolean {
  const { propuesta } = linea;

  if (propuesta === null) return false;

  return !(valor === propuesta.valor && moneda === propuesta.moneda);
}

export interface BorradorDeLinea {
  texto: string;
  moneda: Moneda;
  motivo: string;
  incluidoEn: string;
}

export type PreparacionDelGuardado =
  | { ok: true; cuerpo: CuerpoDelPrecio }
  | { ok: false; error: string | null };

/**
 * El cuerpo del PUT de una línea a partir de lo escrito, o por qué no se puede.
 *
 * `error: null` significa "no hay nada que guardar" (no es una falla que
 * mostrar). Una clave AUSENTE no se toca en el servidor: por eso `incluido_en`
 * solo viaja si cambió, y vaciar el precio manda `null` a propósito.
 */
export function cuerpoDelPrecio(linea: LineaValorizada, borrador: BorradorDeLinea): PreparacionDelGuardado {
  const cuerpo: CuerpoDelPrecio = {};
  const lectura = interpretarPrecio(borrador.texto, borrador.moneda);

  if (lectura.estado === "invalido") return { ok: false, error: lectura.mensaje };

  if (lectura.estado === "ok") {
    cuerpo.precio_unitario = lectura.valor;
    cuerpo.moneda = borrador.moneda;

    if (exigeMotivo(linea, lectura.valor, borrador.moneda)) {
      const motivo = borrador.motivo.trim();

      if (motivo === "") return { ok: false, error: "Indica el motivo: el precio se aparta de la propuesta." };

      cuerpo.motivo = motivo;
    }
  } else if (linea.precio !== null) {
    cuerpo.precio_unitario = null;
  }

  const incluido = borrador.incluidoEn.trim();

  if (incluido !== (linea.incluido_en ?? "")) cuerpo.incluido_en = incluido === "" ? null : incluido;

  return Object.keys(cuerpo).length === 0 ? { ok: false, error: null } : { ok: true, cuerpo };
}

/* ---------------------------------------------------------------------------
   Estado de la fase 2 (carril) y avisos
--------------------------------------------------------------------------- */

const plural = (n: number, singular: string, pluralForma: string) => `${n} ${n === 1 ? singular : pluralForma}`;

/** Los rubros con líneas sin precio o sin cantidad, una sola vez cada uno y en el orden en que llegan. */
export function rubrosPorFijar(valorizacion: Valorizacion): RubroDeValorizacion[] {
  const rubros: RubroDeValorizacion[] = [];

  for (const p of valorizacion.pendientes) {
    if ((p.codigo === "lineas_sin_precio" || p.codigo === "lineas_sin_cantidad") && p.rubro && !rubros.includes(p.rubro)) {
      rubros.push(p.rubro);
    }
  }

  return rubros;
}

export type TonoDeLaFase = "reposo" | "aviso" | "ok";

export function estadoDeLaFase2(valorizacion: Valorizacion | undefined): { tono: TonoDeLaFase; texto: string } {
  if (valorizacion === undefined) return { tono: "reposo", texto: "Sin calcular" };

  const porFijar = rubrosPorFijar(valorizacion).length;

  if (porFijar > 0) return { tono: "aviso", texto: `${plural(porFijar, "rubro", "rubros")} por fijar` };
  if (valorizacion.pendientes.length > 0) {
    return { tono: "aviso", texto: `${plural(valorizacion.pendientes.length, "dato", "datos")} por completar` };
  }
  if (valorizacion.total.uf === null) return { tono: "reposo", texto: "Sin valorizar" };

  return { tono: "ok", texto: "Lista" };
}

/* ---------------------------------------------------------------------------
   Tipo de cambio
--------------------------------------------------------------------------- */

export interface FuenteDelIndicador {
  /** "CMF · dólar observado del 30-06-2025" */
  texto: string;
  /** Solo si el día efectivo no es el pedido (fin de semana, feriado): dd-mm-aaaa. */
  fechaEfectiva: string | null;
}

/**
 * De dónde salió el valor de la UF o del dólar, leído del hecho que lo guarda.
 * La valorización solo trae el número; la fuente vive en la hoja de hechos.
 */
export function fuenteDelTipoDeCambio(hechos: Hecho[], indicador: "uf" | "usd"): FuenteDelIndicador | null {
  const hecho = hechos.find((h) => h.clave === `tipo_cambio_${indicador}`);

  if (!hecho || !hecho.fuente.texto) return null;

  const ref = hecho.fuente.ref as unknown as { fecha_solicitada?: string; fecha_efectiva?: string } | null;
  const difiere = ref?.fecha_efectiva && ref.fecha_solicitada && ref.fecha_efectiva !== ref.fecha_solicitada;

  return {
    texto: hecho.fuente.texto,
    fechaEfectiva: difiere ? ref!.fecha_efectiva!.split("-").reverse().join("-") : null,
  };
}

/** Hoy en Chile (`AAAA-MM-DD`): el servidor rechaza una fecha posterior a la de allá, no a la del reloj local. */
export function hoyEnChile(ahora: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(ahora);
}
